import _ from 'lodash';
import {
  AllegroCreateOrUpdateReturnPolicyRequest,
  AllegroReturnPolicy,
  AllegroReturnPolicyAddress,
  AllegroReturnPolicyAvailability,
  AllegroReturnPolicyAvailabilityRange,
  AllegroReturnPolicyContact,
  AllegroReturnPolicyOptions,
  AllegroReturnPolicyRestrictionCause,
  AllegroReturnPolicyReturnCost,
  AllegroReturnPolicyReturnCostCoveredBy,
} from '../../../api/allegro/returnpolicy/returnPolicyTypes';
import type { ExcelRow } from '../../excel/types';
import { createAllegroReturnPolicy, updateAllegroReturnPolicy } from '../../../api/allegro/returnpolicy/returnPolicy';
import parseExcel from '../../excel/parseExcel';
import COLUMNS from './returnPolicyExcelSchema';
import { showErrorMessage, showSuccessMessage } from '../../../utils/message';
import {
  extractErrorPayload, isAllegroUnauthorized, readBoolean, readString, runTasksSequentially,
} from '../utils';

type UploadTask = () => Promise<AllegroReturnPolicy>;

function mapRestrictionCause(row: ExcelRow): AllegroReturnPolicyRestrictionCause | undefined {
  const restrictionCause = {
    name: readString(row, COLUMNS.restrictionCauseName),
    description: readString(row, COLUMNS.restrictionCauseDescription),
  };

  if (_.every(restrictionCause, _.isUndefined)) {
    return undefined;
  }

  return restrictionCause as AllegroReturnPolicyRestrictionCause;
}

function mapAvailability(row: ExcelRow): AllegroReturnPolicyAvailability {
  return {
    range: readString(row, COLUMNS.range) as AllegroReturnPolicyAvailabilityRange,
    restrictionCause: mapRestrictionCause(row),
  };
}

function mapReturnCost(row: ExcelRow): AllegroReturnPolicyReturnCost | undefined {
  const returnCost = {
    coveredBy: readString(row, COLUMNS.coveredBy) as AllegroReturnPolicyReturnCostCoveredBy,
  };

  if (_.every(returnCost, _.isUndefined)) {
    return undefined;
  }

  return returnCost as AllegroReturnPolicyReturnCost;
}

function mapAddress(row: ExcelRow): AllegroReturnPolicyAddress | undefined {
  const address = {
    name: readString(row, COLUMNS.name),
    street: readString(row, COLUMNS.street),
    postCode: readString(row, COLUMNS.postCode),
    city: readString(row, COLUMNS.city),
    countryCode: readString(row, COLUMNS.countryCode),
  };

  if (_.every(address, _.isUndefined)) {
    return undefined;
  }

  return address as AllegroReturnPolicyAddress;
}

function mapContact(row: ExcelRow): AllegroReturnPolicyContact | undefined {
  const email = readString(row, COLUMNS.email);
  const phoneNumber = readString(row, COLUMNS.phoneNumber);

  const contact = {
    email,
    phoneNumber,
  };

  if (_.every(contact, _.isUndefined)) {
    return undefined;
  }

  return contact;
}

function mapOptions(row: ExcelRow): AllegroReturnPolicyOptions | undefined {
  const options = {
    cashOnDeliveryNotAllowed: readBoolean(row, COLUMNS.cashOnDeliveryNotAllowed),
    freeAccessoriesReturnRequired: readBoolean(row, COLUMNS.freeAccessoriesReturnRequired),
    refundLoweredByReceivedDiscount: readBoolean(row, COLUMNS.refundLoweredByReceivedDiscount),
    businessReturnAllowed: readBoolean(row, COLUMNS.businessReturnAllowed),
    collectBySellerOnly: readBoolean(row, COLUMNS.collectBySellerOnly),
  };

  if (_.every(options, _.isUndefined)) {
    return undefined;
  }

  return options as AllegroReturnPolicyOptions;
}

function mapRequest(row: ExcelRow): AllegroCreateOrUpdateReturnPolicyRequest {
  return {
    name: readString(row, COLUMNS.label),
    availability: mapAvailability(row),
    withdrawalPeriod: readString(row, COLUMNS.withdrawalPeriod),
    returnCost: mapReturnCost(row),
    address: mapAddress(row),
    contact: mapContact(row),
    options: mapOptions(row),
  } as AllegroCreateOrUpdateReturnPolicyRequest;
}

function prepareUploadTasks(marketplaceCode: string, rows: ExcelRow[]): UploadTask[] {
  return rows.map((row) => {
    const id = readString(row, COLUMNS.id);

    if (id) {
      return () => updateAllegroReturnPolicy(marketplaceCode, id, mapRequest(row));
    }

    return () => createAllegroReturnPolicy(marketplaceCode, mapRequest(row));
  });
}

export default async function uploadReturnPoliciesExcel(
  marketplaceCode: string,
  file: File,
): Promise<void> {
  const rows = await parseExcel(file);
  const tasks = prepareUploadTasks(marketplaceCode, rows);
  const errors: any[] = [];

  await runTasksSequentially(
    tasks,
    (reason) => {
      if (isAllegroUnauthorized(reason)) {
        return;
      }

      errors.push(extractErrorPayload(reason));
    },
  );

  if (errors.length === 0) {
    showSuccessMessage('Return policies successfully uploaded.');
  } else {
    showErrorMessage(JSON.stringify(errors));
  }
}
